import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  IconButton,
  Button,
  Modal,
  Fade,
  Backdrop,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useApi } from "../../../api/useApi";
import { recordLevelReportById, rlrStatus } from "../../../api/reportQueries";
import Loading from "./Loading";
import Error from "./Error";
import { formatTime } from "../../../common/formats";
import { FaFileCsv } from "react-icons/fa";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import BCALogo from "../../../components/Logos/BCALogo";
import styled from "styled-components/macro";
import { faker } from "@faker-js/faker";
import { format } from "date-fns";

const BCALogoComp = styled(BCALogo)`
  width: 21em;
  position: absolute;
  margin: 3em;
`;

const CertContainer = styled.div`
  font-family: "Gotham-Book";
`;

const generateMockLoans = () => {
  const loans = [];
  // "Conventional", "FHA", "VA", "Jumbo", "USDA",
  const loanTypes = ["Heloc"];
  const loanPurposes = ["Purchase", "Refinance", "Cash-Out Refi"];
  const propertyTypes = ["Single Family", "Condo", "Townhouse", "Multi-Family"];
  const occupancyTypes = ["Primary", "Secondary", "Investment"];

  for (let i = 0; i < 100; i++) {
    const id = faker.string.numeric(9);
    const score = faker.number.int({ min: 60, max: 110 });
    const status = score < 80 ? "Failed" : "Qualified";

    let policies = [
      { name: "Rate Match Note", state: "PASS" },
      { name: "Property Match Note", state: "PASS" },
      { name: "LTV to DTI", state: "PASS" },
      { name: "Note Present", state: "PASS" },
      { name: "Patriot Disclosure Present", state: "PASS" },
    ];

    // Randomly fail some policies if the loan fails
    if (status === "Failed") {
      const numFailures = faker.number.int({ min: 1, max: policies.length });
      const failedIndices = new Set();

      while (failedIndices.size < numFailures) {
        failedIndices.add(
          faker.number.int({ min: 0, max: policies.length - 1 })
        );
      }

      policies = policies.map((policy, index) => ({
        ...policy,
        state: failedIndices.has(index) ? "FAIL" : "PASS",
      }));
    }

    const loan = {
      id,
      status,
      policies,
      data: {
        loanAmount: faker.finance.amount(100000, 500000, 2),
        interestRate: faker.finance.amount(2.0, 5.0, 2),
        term: faker.helpers.arrayElement([15, 20, 30]),
        loanType: faker.helpers.arrayElement(loanTypes),
        loanPurpose: faker.helpers.arrayElement(loanPurposes),
        loanDate: faker.date.past({ years: 1 }).toISOString().split("T")[0],
        closingDate: faker.date
          .future({ years: 1 })
          .toISOString()
          .split("T")[0],
        propertyType: faker.helpers.arrayElement(propertyTypes),
        occupancy: faker.helpers.arrayElement(occupancyTypes),
        borrowerName: faker.person.fullName(),
        borrowerEmail: faker.internet.email(),
        borrowerPhone: faker.phone.number(),
        propertyAddress: faker.location.streetAddress({ useFullAddress: true }),
        ficoScore: faker.number.int({ min: 620, max: 850 }),
        ltv: faker.finance.amount(50, 100, 1) + "%",
      },
    };

    loans.push(loan);
  }

  return loans;
};

const LoanDetailsModal = ({
  open,
  handleClose,
  loan: loanData,
  handleBack,
  data,
}) => {
  const loan = loanData.loan;
  const [showPolicyTable, setShowPolicyTable] = useState(true);
  const [certModalOpen, setCertModalOpen] = useState(
    loanData?.openCert ? true : false
  );
  const certRef = useRef(null);

  const handleDownloadPDF = async () => {
    const element = certRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("l", "mm", "a4"); // "l" for landscape
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth; // Full page width
    const imgHeight = (canvas.height * imgWidth) / canvas.width; // Scale to maintain aspect ratio

    pdf.addImage(
      imgData,
      "PNG",
      0,
      (pageHeight - imgHeight) / 2,
      imgWidth,
      imgHeight
    );
    pdf.save(`Certificate_${loan.id}.pdf`);
  };

  return (
    <>
      {/* Loan Details Modal */}
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Fade in={open}>
          <Box
            sx={{
              width: "60%",
              margin: "auto",
              mt: 5,
              p: 3,
              backgroundColor: "white",
              boxShadow: 24,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Loan Details - {loan.id}
            </Typography>

            {showPolicyTable ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Policy</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loan.policies.map((policy, index) => {
                      const isFailed = policy.state === "FAIL";
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: isFailed ? "#fff" : "#e8f5e9",
                          }}
                        >
                          <TableCell>{policy.name}</TableCell>
                          <TableCell>{policy.state}</TableCell>
                          <TableCell align="right">
                            <Button variant="outlined">View Policy</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 400px)",
                    mb: 1,
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(loan.data).map(([key, value], index) => (
                        <TableRow key={index}>
                          <TableCell>{key}</TableCell>
                          <TableCell
                            style={{
                              color:
                                loan.status === "Failed" && Math.random() < 0.4
                                  ? "red"
                                  : "auto",
                            }}
                          >
                            {value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Button variant="contained" onClick={handleBack}>
                Back
              </Button>

              <Button
                variant="outlined"
                onClick={() => setShowPolicyTable((prev) => !prev)}
              >
                {showPolicyTable ? "View Details" : "View Policies"}
              </Button>

              <Button
                variant="contained"
                onClick={() => setCertModalOpen(true)}
                disabled={loan?.status === "Failed"}
              >
                View Cert
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Certificate Modal */}
      <Modal
        open={certModalOpen}
        onClose={() => setCertModalOpen(false)}
        closeAfterTransition
      >
        <Fade in={certModalOpen}>
          <Box ref={certRef}>
            <CertContainer>
              <Box
                className="certBackground cert"
                sx={{
                  width: "842px",
                  height: "595px",
                  margin: "auto",
                  mt: 5,
                  position: "relative",
                  zIndex: 2,
                  padding: "24px",
                }}
              >
                <Box
                  sx={{
                    color: "white",
                    textAlign: "left",
                    height: "100%",
                    position: "relative",
                    padding: "24px",
                    border: "solid 1px white",
                    borderRadius: "10px",
                  }}
                >
                  <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontWeight: "bold", fontFamily: "Gotham-Book" }}
                  >
                    CERTIFICATE OF REVIEW
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      paddingBottom: "24px",
                      fontFamily: "Gotham-Thin",
                      fontSize: "1.1rem",
                    }}
                  >
                    This document certifies that diligence has been performed
                    for the following loan.
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 5,
                      fontFamily: "Gotham-Book",
                      fontSize: "2rem",
                      borderBottom: "1px solid white",
                      display: "inline",
                    }}
                  >
                    HELOC LOAN #{loan.id}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: "35px",
                      fontFamily: "Gotham-Thin",
                      fontSize: "1.2rem",
                    }}
                  >
                    And they are designated:{" "}
                    <strong style={{ fontFamily: "Gotham-BookItalic" }}>
                      Qualified Level 1
                    </strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1,
                      fontFamily: "Gotham-Thin",
                      fontSize: "1.2rem",
                    }}
                  >
                    For the due diligence checks:{" "}
                    <strong style={{ fontFamily: "Gotham-BookItalic" }}>
                      Asset Fact Review, Fraud Detection
                    </strong>
                  </Typography>
                  <Typography
                    variant="body"
                    sx={{
                      mt: 1,
                      position: "absolute",
                      top: "24rem",
                      left: "24px",
                      fontFamily: "Gotham-Thin",
                    }}
                  >
                    Certified by BaseCap Analytics, Inc
                  </Typography>
                  <div
                    style={{
                      position: "absolute",
                      right: "2rem",
                      top: "25rem",
                    }}
                  >
                    <div style={{ textAlign: "right" }}>
                      <Typography
                        variant="body"
                        sx={{
                          mt: 1,
                          display: "block",
                          fontFamily: "Gotham-Thin",
                          fontSize: ".7rem",
                        }}
                      >
                        Diligence performed for: ______
                        <span>{`${data?.performedFor}`}</span>________
                      </Typography>
                      <Typography
                        variant="body"
                        sx={{
                          mt: 4,
                          display: "block",
                          fontFamily: "Gotham-Thin",
                          fontSize: ".7rem",
                        }}
                      >
                        Issuance Date: _________
                        {`${format(new Date(), "MMM dd yyyy")}`}
                        _____________
                      </Typography>
                    </div>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "24rem",
                      left: "-24px",
                      width: "12rem",
                    }}
                  >
                    <BCALogoComp />
                  </div>
                </Box>
              </Box>
            </CertContainer>
            {/* Fixed Button Bar */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                p: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={handleDownloadPDF}
                sx={{ mr: 2 }}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCertModalOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

const Certifications = ({ data }) => {
  const [dynamicViewData, setDynamicViewData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(data?.pageSize ?? 3);
  const [isLoading, setIsLoading] = useState(true);
  const [idsToFetch, setIdsToFetch] = useState(data?.dynamicView || []); // Initialize with an empty array if undefined
  const [fetchedIds, setFetchedIds] = useState(new Set()); // Track fetched IDs to avoid duplicates
  const [openModal, setOpenModal] = useState(false);
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [loanDetailsData, setLoanDetailsData] = useState(generateMockLoans());
  const [selectedLoan, setSelectedLoan] = useState(null);

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const filteredLoans = useMemo(() => {
    return loanDetailsData.filter((loan) => {
      const matchesId = loan.id.includes(searchTerm.trim());
      const matchesStatus =
        statusFilter === "All" || loan.status === statusFilter;
      return matchesId && matchesStatus;
    });
  }, [searchTerm, loanDetailsData, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedLoans = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLoans.slice(start, start + rowsPerPage);
  }, [filteredLoans, page]);

  const [{ errors: errorsReport, data: reportDataQuery }, fetchReportById] =
    useApi();
  const [{ errors: errorsStatus, data: statusDataQuery }, fetchRlrStatus] =
    useApi();

  useEffect(() => {
    if (data?.pageSize) {
      setPageSize(data.pageSize);
    }
  }, [data]); // This will run whenever `data` changes

  // Fetch the data for each ID sequentially
  useEffect(() => {
    const fetchSequentially = async () => {
      if (!idsToFetch || idsToFetch.length === 0) {
        setIsLoading(false); // Stop loading if no more IDs to fetch
        return;
      }

      const id = idsToFetch[0]; // Get the first ID to fetch

      // Skip fetching if this ID has already been fetched
      if (fetchedIds.has(id)) {
        setIdsToFetch((prevIds) => prevIds.slice(1)); // Remove the fetched ID
        return;
      }

      // Fetch the recordLevelReportById and rlrStatus in sequence (but not in parallel)
      await fetchReportById({
        query: recordLevelReportById,
        variables: { id },
      });
      await fetchRlrStatus({
        query: rlrStatus,
        variables: { rlrId: id },
      });
    };

    if (idsToFetch.length > 0) {
      fetchSequentially();
    } else {
      setIsLoading(false); // Ensure isLoading is set to false when no more data is left to fetch
    }
  }, [idsToFetch, fetchReportById, fetchRlrStatus, fetchedIds]);

  // Process reportDataQuery and statusDataQuery once they are fetched
  useEffect(() => {
    if (reportDataQuery && statusDataQuery) {
      const reportData = reportDataQuery.recordLevelReportById;
      const statusData = statusDataQuery.rlrStatus;

      // Only add to dynamicViewData if the ID hasn't been processed yet
      if (!fetchedIds.has(reportData.id)) {
        setDynamicViewData((prevData) => [
          ...prevData,
          {
            ...reportData,
            status: statusData, // Add the status from rlrStatus
          },
        ]);
        setFetchedIds((prev) => new Set(prev).add(reportData.id));
        setIdsToFetch((prevIds) => prevIds.slice(1)); // Remove the fetched ID from idsToFetch
      }
    }
  }, [reportDataQuery, statusDataQuery, fetchedIds]);

  useEffect(() => {
    if (data?.dynamicView) {
      setIdsToFetch(data.dynamicView);
    } else {
      setIsLoading(false);
    }
  }, [data?.dynamicView]);

  if (isLoading) {
    return <Loading />;
  }

  if (errorsReport || errorsStatus) {
    return <Error message="There was an error loading the data." />;
  }

  // Function to convert data to CSV format
  const downloadCSV = () => {
    const headers = ["Report Name", "Last Updated Date", "Failure Count"];
    const rows = dynamicViewData.map((view) => [
      view.name,
      view.status?.lastUpdatedTime
        ? formatTime(new Date(view.status?.lastUpdatedTime))
        : "N/A",
      view.status?.totalFailures ?? 0,
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((e) => e.join(",")).join("\n");

    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10); // YYYY-MM-DD format

    // Create a download link and trigger it
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `${
        data?.widgetName ? data.widgetName : "dynamic_view_data"
      }_${formattedDate}.csv`
    );
    document.body.appendChild(link); // Required for Firefox
    link.click();
    document.body.removeChild(link); // Clean up
  };

  // Calculate the total number of pages
  const totalPages = Math.ceil(dynamicViewData.length / pageSize);

  // Slice the data for the current page
  const currentPageData = dynamicViewData.slice(
    pageIndex * pageSize,
    pageIndex * pageSize + pageSize
  );

  const handlePageChange = (event, newPage) => {
    setPageIndex(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageIndex(0); // Reset to first page on page size change
  };

  const overviewData =
    data?.pools?.map((pool, i) => ({
      pool: pool?.poolName,
      percentage: pool?.poolPercentage,
      status: pool?.poolStatus,
      color: pool?.poolColor,
    })) ?? [];

  const actionOnLoansData = [
    { action: "Move to Servicing", count: 53 },
    { action: "High Risk Review", count: 2 },
    { action: "Package for Sale", count: 87 },
    { action: "Corrections", count: 8 },
  ];

  const issuesToAddressData = [
    { issue: "Docs Missing", count: 1 },
    { issue: "Doc Read Issues", count: 8 },
    { issue: "Loan Details", count: 2 },
    { issue: "Property Details", count: 8 },
    { issue: "Escrow", count: 3 },
    { issue: "Borrower", count: 5 },
  ];

  const handleOpenModal = () => {
    setSelectedLoans(loanDetailsData);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const handleBack = () => {
    setSelectedLoan(null);
  };
  return (
    <>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ zIndex: 2, position: "relative", top: "-10px" }}
      >
        {data?.widgetName}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        {/* First row with two tables side by side */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TableContainer component={Paper} sx={{ flex: 1, minWidth: 300 }}>
            <Typography
              variant="h6"
              sx={{ p: 2, backgroundColor: "#2a3b66", color: "white" }}
            >
              Overview
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Pool Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Passing Rate
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>State</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overviewData?.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.pool}</TableCell>
                    <TableCell>{row.percentage}</TableCell>
                    <TableCell sx={{ color: row.color }}>
                      {row.status}
                    </TableCell>
                    <TableCell sx={{ color: row.color }}>
                      <Button variant="contained" onClick={handleOpenModal}>
                        View Certifications
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} sx={{ flex: 1, minWidth: 300 }}>
            <Typography
              variant="h6"
              sx={{ p: 2, backgroundColor: "#2a3b66", color: "white" }}
            >
              Action on Loans
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Action State
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Loan Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionOnLoansData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.action}</TableCell>
                    <TableCell sx={{ color: "blue" }}>{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Full-width table below */}
        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            sx={{ p: 2, backgroundColor: "#2a3b66", color: "white" }}
          >
            Issues to Address
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Policy Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Loan Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issuesToAddressData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.issue}</TableCell>
                  <TableCell>{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Certification Modal */}
      <Modal open={openModal} onClose={handleCloseModal} closeAfterTransition>
        <Fade in={openModal}>
          <Box
            sx={{
              width: "80%",
              margin: "auto",
              mt: 5,
              p: 3,
              backgroundColor: "white",
              boxShadow: 24,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Loan Certification Details
            </Typography>

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search Loan Id"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => {
                  setPage(0);
                  setSearchTerm(e.target.value);
                }}
              />
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  label="Status Filter"
                  value={statusFilter}
                  onChange={(e) => {
                    setPage(0);
                    setStatusFilter(e.target.value);
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Qualified">Qualified</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: "calc(100vh - 400px)",
                mb: 1,
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Loan Id</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLoans.map((loan, index) => {
                    const isFailed = loan.status === "Failed";
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: isFailed ? "#fff" : "#e8f5e9", // yellow or green
                        }}
                      >
                        <TableCell>
                          <div
                            style={{ color: "blue", cursor: "pointer" }}
                            onClick={() =>
                              setSelectedLoan({
                                loan: { ...loan },
                                openCert: false,
                              })
                            }
                          >
                            {loan.id}
                          </div>
                        </TableCell>
                        <TableCell>{loan.status}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            disabled={loan?.status === "Failed"}
                            sx={
                              isFailed
                                ? {
                                    backgroundColor: "#fff",
                                    color: "black",
                                    fontWeight: "bold",
                                  }
                                : {}
                            }
                            onClick={() => {
                              setSelectedLoan({
                                loan: { ...loan },
                                openCert: true,
                              });
                            }}
                          >
                            View Cert
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredLoans.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />

            {selectedLoan && (
              <LoanDetailsModal
                open={openModal}
                handleClose={handleBack}
                loan={selectedLoan}
                handleBack={handleBack}
                data={data}
              />
            )}

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleCloseModal}
            >
              Close
            </Button>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default Certifications;
